import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faUpload } from '@fortawesome/pro-regular-svg-icons';
export const defaultTypes = 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/rtf,application/octet-stream,application/vnd.dwg';
export const defaultTypesArray = defaultTypes.split(',');
export const unsupportedFileError = `This is not a supported file type. Please upload a PDF, DOCX, TXT, XLSX, PPTX, RTF, BIM, or CAD.`;
export const genericError = `Something went wrong. Please try uploading again.`;
const AttachmentButton = ({
  onClick,
  icon,
  buttonClass,
  iconClass,
  text,
  textClass
}) => {
  return <button className={buttonClass} onClick={onClick}>
      <FontAwesomeIcon className={iconClass} icon={icon} role="img" title={text} />
      <span className={`button-text ${textClass}-image-btn--text`}>{text}</span>
    </button>;
};
const DeleteAttachment = ({
  removeFile
}) => {
  return <AttachmentButton onClick={removeFile} icon={faTrashAlt} buttonClass="foundry--button photo-upload--delete" iconClass="delete-image-btn--icon" text="Delete Attachment" textClass="delete" />;
};
const ChangeAttachment = ({
  fileInput
}) => {
  return <AttachmentButton onClick={() => fileInput.current.click()} icon={faUpload} buttonClass="foundry--button photo-upload--change" iconClass="change-image-btn--icon" text="Change Attachment" textClass="change" />;
};
export const AttachmentInfo = ({
  file,
  sizeLimit,
  fileInput,
  removeFile,
  reportError = () => {}
}) => {
  const {
    name,
    size,
    type
  } = file;
  return <div className="photo-upload--single-photo--container">
      <PreviewAttachment file={file} type={type} name={name} size={size} sizeLimit={sizeLimit} reportFileError={reportError} />
      <div className="photo-upload--wrapper">
        <div className="photo-upload-file--name">{name}</div>
        <div className="photo-upload--btns">
          <ChangeAttachment fileInput={fileInput} />
          <DeleteAttachment removeFile={removeFile} />
        </div>
      </div>
    </div>;
};
const PreviewAttachment = ({
  file,
  size,
  type,
  sizeLimit,
  name,
  acceptedTypes = defaultTypesArray,
  removeFile,
  reportFileError = () => {}
}) => {
  const sizeLimitInBytes = sizeLimit * 1024 * 1024;
  const {
    url
  } = file;
  const [AttachmentUrl, setAttachmentUrl] = useState();
  const [errorState, setErrorState] = useState('');
  const overSize = size > sizeLimitInBytes;
  const wrongType = acceptedTypes.indexOf(type) === -1;
  const fileTooLargeError = `This file is too large. Files cannot exceed ${sizeLimit}MB.`;
  const tagError = msg => {
    reportFileError(true);
    setErrorState(msg);
  };
  const unTagError = () => {
    reportFileError(false);
    setErrorState('');
  };
  useEffect(() => {
    if (file) {
      unTagError();
      if (overSize) {
        tagError(fileTooLargeError);
        return unTagError;
      } else if (wrongType) {
        tagError(unsupportedFileError);
        return unTagError;
      } else if (file instanceof File || url) {
        const reader = new FileReader();
        reader.onload = e => {
          setAttachmentUrl(e.target.result);
        };
        reader.onerror = () => {
          tagError(genericError);
        };
        try {
          const blobFile = url ? fetch(url).then(res => {
            return res.blob();
          }) : Promise.resolve(file);
          blobFile.then(blob => {
            reader.readAsDataURL(blob);
          }).catch(() => {
            tagError(genericError);
          });
        } catch (e) {
          tagError(genericError);
        }
        return () => {
          unTagError();
          reader.abort();
        };
      }
    }
  }, [file]);
  return <div className="photo-upload--preview-image photo-upload--image-complete">
      <embed className="photo-upload--preview-image photo-upload--image-complete" src={AttachmentUrl} title={name} onError={() => tagError(genericError)} width="100%" height="500px" />
    </div>;
};
export const VisualDropArea = ({
  limit = 1,
  inputRef,
  errorMessage,
  sizeLimit
}) => {
  const [hasDragOver, setDragOverState] = useState('');

  // handling the detection of events over the input, for visual changes
  useEffect(() => {
    const dragOn = e => {
      if (e.target === inputRef.current) {
        setDragOverState(true);
      } else {
        setDragOverState(false);
      }
    };
    const dragOff = e => {
      if (e.target === inputRef.current) {
        setDragOverState(false);
      }
    };
    const dragOverInput = () => {
      setDragOverState(true);
    };
    const dragOffInput = () => {
      setDragOverState(false);
    };

    // add drop event to cancel file drops outside the input
    inputRef.current.addEventListener('dragenter', dragOverInput, false);
    inputRef.current.addEventListener('dragleave', dragOffInput, false);
    document.addEventListener('drop', dragOff, false);
    document.addEventListener('dragend', dragOff, false);
    document.addEventListener('dragover', dragOn, false);
    return () => {
      // remove event canceling drop events
      document.removeEventListener('drop', dragOff);
      document.removeEventListener('dragend', dragOff);
      document.removeEventListener('dragover', dragOn);
      if (inputRef.current) {
        inputRef.current.removeEventListener('dragenter', dragOverInput, false);
        inputRef.current.removeEventListener('dragleave', dragOffInput, false);
      }
    };
  }, []);
  const className = `photo-upload--drop-container${hasDragOver ? ` has-dragover` : ``}${errorMessage ? ' has-error' : ``}`;
  const uploadRules = ['Supported file types: PDF, DOCX, TXT, XLSX, PPTX, RTF, BIM, CAD', `Images must be less than ${sizeLimit}MB in size`];
  if (limit > 1) {
    uploadRules.unshift(`${limit} images max`);
  }
  return <div className={className}>
      <div className="photo-upload--wrapper">
        <div className="photo-upload--text-area">
          {`Drop your Attachment here`} or <span>browse</span>
        </div>
        <ul className="photo-upload--rules">
          {uploadRules.map(rule => <li key={rule}>{rule}</li>)}
        </ul>
      </div>
    </div>;
};
export const ClientAttachmentUpload = ({
  value = [],
  accept = defaultTypes,
  limit = 1,
  closeModal,
  update,
  sizeLimit
}) => {
  const sizeLimitInBytes = sizeLimit * 1024 * 1024;
  const fileInput = useRef();
  const [currentFiles, setCurrentFiles] = useState(value || []);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasFileError, setHasFileError] = useState(false);
  const acceptedTypes = accept.split(',');
  const isEmpty = !currentFiles || !currentFiles.length;
  const showDropArea = currentFiles && currentFiles.length !== limit || isEmpty;
  const updateFileInput = file => {
    const dt = new DataTransfer();
    if (file instanceof File) {
      dt.items.add(file);
    }
    fileInput.current.files = dt.files;
  };
  const removeFile = fileToRemove => {
    if (!currentFiles || !currentFiles.length) {
      setCurrentFiles(null);
      return false;
    }
    const cleanedFiles = currentFiles.filter(file => file !== fileToRemove);
    // this isn't supported on pre 14 iOS/Safari but there's no alternate way
    // do it for those browsers except to clear the whole input
    if (!cleanedFiles.length) {
      fileInput.current.value = '';
      setErrorMessage('');
    } else {
      updateFileInput(cleanedFiles);
    }
    setCurrentFiles(cleanedFiles);
  };

  // checking the file added for various errors
  const onFileChange = event => {
    const file = event.target.files[0];
    const fileTooLargeError = `This file is too large. Files cannot exceed ${sizeLimit}MB.`;
    if (currentFiles && currentFiles.length > 0) {
      removeFile(currentFiles[0]);
    }
    const overSize = file.size > sizeLimitInBytes;
    const wrongType = acceptedTypes.indexOf(file.type) === -1;
    if (overSize) {
      event.preventDefault();
      fileInput.current.value = '';
      setErrorMessage(fileTooLargeError);
      return false;
    } else if (wrongType) {
      event.preventDefault();
      removeFile(file);
      setErrorMessage(unsupportedFileError);
      return false;
    } else {
      // Clear any previous error messages
      setErrorMessage('');

      // Update the current files state
      setCurrentFiles([file]);
      updateFileInput([file]);
    }
  };

  // saving blob urls to the store instead of entire images
  const saveBlobs = () => {
    if (currentFiles && currentFiles.length) {
      const values = currentFiles.map(file => {
        const {
          url,
          size,
          type,
          name
        } = file;
        return {
          url: url || URL.createObjectURL(file),
          size,
          type,
          name
        };
      });
      update(values);
    } else {
      update(null);
    }
  };
  const captureAttachment = () => {
    saveBlobs();
    closeModal();
  };

  // we want to prevent accidental drops outside the file inputs
  // from replacing the entire page with the dropped image
  useEffect(() => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    const maybeCancelDropEvent = e => {
      const {
        dataTransfer
      } = e;
      if (e.target !== fileInput.current) {
        e.preventDefault();
        dataTransfer.effectAllowed = 'none';
        dataTransfer.dropEffect = 'none';
        return false;
      } else {
        dataTransfer.effectAllowed = 'all';
        dataTransfer.dropEffect = 'move';
      }
    };

    // add drop event to cancel file drops outside the input
    document.addEventListener('drop', maybeCancelDropEvent, false);
    document.addEventListener('dragover', maybeCancelDropEvent, false);
    return () => {
      // remove event canceling drop events
      document.removeEventListener('drop', maybeCancelDropEvent);
      document.removeEventListener('dragover', maybeCancelDropEvent);
    };
  }, []);
  const hasError = hasFileError || String(errorMessage).trim();
  return <div className="portal-modal--ui-container">
      <div className="portal-modal--intro">
        <h2 className="portal-modal--heading">Capture Attachment</h2>
        {!isEmpty && <div className="photo-upload--count">
            {currentFiles.length} of {limit}
          </div>}
      </div>
      <div className="photo-upload--area">
        {showDropArea && <VisualDropArea limit={limit} inputRef={fileInput} errorMessage={errorMessage} sizeLimit={sizeLimit} />}
        <input ref={fileInput} type="file" onChange={onFileChange} onError={e => setErrorMessage(e.message)} accept={accept} className="photo-upload--input" />
      </div>
      {!isEmpty && <AttachmentInfo sizeLimit={sizeLimit} file={currentFiles[0]} removeFile={() => {
      removeFile(currentFiles[0]);
    }} fileInput={fileInput} reportError={isError => {
      if (isError) {
        removeFile(currentFiles[0]);
        update(null);
        setErrorMessage(genericError);
      }
    }} />}
      {!!String(errorMessage).trim() && <div className="portal-modal--error">
          <i className="foundry-icon icon--exclamation-triangle icon--red"></i>
          {errorMessage}
        </div>}
      <div className="portal-modal--controls">
        <hr />
        <button className="foundry--button btn--link" onClick={closeModal}>
          Cancel
        </button>
        <button className={`foundry--button button--contained ${hasError ? `button--disabled` : `button--active`}`} disabled={hasError} onClick={captureAttachment}>
          Capture Attachment
        </button>
      </div>
    </div>;
};